import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import { andOnlyPlacementError, valueStringsOf, type FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import { validateOperatorValueShape } from '@shared/flow/filter-operators';

/**
 * `useFilterDraft` — the staged filter-editing state that used to live
 * entirely inside `FilterPopover` (QA-2, plan V2), extracted so it can be
 * OWNED BY THE CALLER instead (GTMOPS-2216).
 *
 * Why this moved: `FilterPopover`'s own module doc explains that the draft
 * used to live there specifically BECAUSE `ResponsivePopover` unmounts its
 * children on close — closing without Apply discarded the draft "for free."
 * That was a deliberate design (docs/flow/custom-views-notes.md's
 * "Apply-filters staging" section: "a rep who opens the popover, types
 * something, and clicks away loses that edit — this is intentional"). Spencer
 * reversed that call for GTMOPS-2216: a closed-but-unapplied draft must
 * survive the close and offer Discard/Edit/Apply through a persistent banner
 * (`FilterDraftBox`) — which is impossible if the draft's state is destroyed
 * the moment the popover unmounts. Lifting ownership up to whichever
 * component OUTLIVES the popover (`FlowTable`) is the only way to keep the
 * draft alive across a close/reopen cycle.
 *
 * `FilterPopover` still calls this hook itself when no external `draft` is
 * supplied (see its module doc) — every existing direct-render test
 * (`FilterPopover.test.tsx`) exercises that self-contained path and its
 * behavior is unchanged: draft state seeded from `filter`/`groupLogic`,
 * reseeded whenever those props change VALUE from an outside source (view
 * switch, role-default seeding), and validated the same way. `FlowTable`
 * additionally calls this hook itself, once, and hands the result down as
 * `FilterPopover`'s `draft` prop AND to `FilterDraftBox` — the one instance
 * is then the single source of truth for both, so the popover and the
 * banner can never disagree about what the draft currently holds.
 */
export interface UseFilterDraftOptions {
  filter: FilterGroup[];
  onFiltersChange: (filter: FilterGroup[]) => void;
  groupLogic: GroupLogic;
  onGroupLogicChange: (logic: GroupLogic) => void;
  fields: FilterFieldCatalogEntry[];
  /**
   * Commit the draft in ONE call, groups and logic together. When set, `apply` calls
   * this instead of `onFiltersChange` / `onGroupLogicChange` — for a caller whose
   * filter is a single value, where two back-to-back setters would let the second
   * overwrite the first with stale state.
   */
  onApply?: (filters: FilterGroup[], groupLogic: GroupLogic) => void;
  /**
   * A caller's own check on the whole draft (e.g. a rule that spans groups), consulted
   * after every condition passes the per-condition shape check. Its message gates Apply
   * like a shape error does.
   */
  validate?: (groups: FilterGroup[]) => string | null;
  /**
   * For a caller whose Apply rewrites the draft's groups (merges two into one, renames ids): once the
   * applied value that Apply produces arrives, the draft becomes exactly that value instead of being
   * rebased onto it, because a rebase keeps every draft-only group and would leave the merged-away
   * originals behind as duplicates. Other changes to the applied value still rebase.
   */
  replaceDraftOnApply?: boolean;
}

export interface FilterDraft {
  draftFilters: FilterGroup[];
  draftGroupLogic: GroupLogic;
  setDraftFilters: (updater: FilterGroup[] | ((prev: FilterGroup[]) => FilterGroup[])) => void;
  setDraftGroupLogic: (logic: GroupLogic) => void;
  /** Draft differs (by value) from the currently APPLIED `filter`/`groupLogic`. */
  isDirty: boolean;
  /**
   * The first value-shape error in the draft (`validateOperatorValueShape`),
   * or null. Gates both `FilterPopover`'s own Apply button and
   * `FilterDraftBox`'s Apply — a malformed shape (e.g. an empty `in_any_of`,
   * a half-filled `between`) must never be committed, closed popover or not
   * (Finding 2, ported verbatim).
   */
  validationError: string | null;
  /** Commit the draft as the new applied filter/groupLogic. */
  apply: () => void;
  /** Revert the draft back to the currently applied filter/groupLogic. */
  discard: () => void;
}

/** Value-comparison key for the staged draft — ported verbatim from
 * `FilterPopover`'s original `filterDraftKey`. */
function filterDraftKey(groups: FilterGroup[], logic: GroupLogic): string {
  return JSON.stringify({ groups, logic });
}

/**
 * Codex round-1 finding #5 — rebase an unapplied DRAFT onto a new applied
 * filter value instead of replacing it wholesale.
 *
 * The failure this fixes: Search writes the applied filters (it owns the
 * `play_name contains` / contact search chip group), so typing into the box
 * while a staged-but-unapplied condition sat in the draft made the reseed
 * effect below overwrite that condition — the rep's in-progress filter, and
 * the `FilterDraftBox` banner advertising it, both vanished mid-keystroke.
 *
 * Everything is keyed by GROUP ID, never by array position: the search chip
 * lands at whatever index `withPlayNameSearch`/its contact equivalent puts
 * it, so positional merging would mis-pair unrelated groups.
 *
 *  - id in `next` but not `prev` → APPENDED (an upstream addition the draft
 *    has never seen — this is the search chip's path).
 *  - id in `prev` but not `next` → REMOVED (upstream deleted it; keeping it
 *    would resurrect a filter the rep no longer has applied).
 *  - id in both, content CHANGED upstream → the applied version REPLACES the
 *    draft's. Upstream wins a genuine conflict; the rep can still re-stage.
 *  - id in both, content UNCHANGED → the DRAFT's version survives. This is
 *    the whole point of the function: the staged edit lives here.
 *  - id in the DRAFT ONLY, i.e. never present in `prevApplied` (a brand-new
 *    group the rep staged) → PRESERVED (Codex round-2 **#3**, plan amendment
 *    **A21**).
 *
 * That last rule is the round-2 fix. The previous version dropped EVERY
 * draft group absent from `nextApplied`, including one `prevApplied` had
 * never contained — so with zero filters applied, staging a Start Date group
 * and then typing in Search (which rewrites the applied filters) rebased
 * `prev=[]`, `next=[search]`, `draft=[start-date]` down to `[search]`: the
 * staged group and the `FilterDraftBox` banner advertising it both vanished
 * mid-keystroke. The same loss hit any brand-new draft group regardless of
 * how many filters were already applied.
 *
 * The distinction that makes preserving it safe: "absent from `next`" is only
 * evidence of an UPSTREAM REMOVAL when `prev` actually had the group. A group
 * `prev` never had is something upstream has no opinion about, so there is
 * nothing to rebase onto and nothing that justifies deleting the rep's work.
 *
 * This is deliberately NOT how a view switch discards an in-progress edit any
 * more — a rebase cannot tell "the search text changed" from "replace the
 * whole view" by inspecting the values, so that discard needs an explicit
 * reset action from the caller rather than being inferred here. Inferring it
 * is what cost the rep their staged group.
 *
 * Pure and exported for its own unit tests (`useFilterDraft.test.ts`).
 */
export function rebaseDraft(
  prevApplied: FilterGroup[],
  nextApplied: FilterGroup[],
  draft: FilterGroup[],
): FilterGroup[] {
  const prevById = new Map(prevApplied.map(g => [g.id, g]));
  const nextById = new Map(nextApplied.map(g => [g.id, g]));

  const rebased: FilterGroup[] = [];
  for (const group of draft) {
    const next = nextById.get(group.id);
    if (next) {
      const prev = prevById.get(group.id);
      const changedUpstream = prev != null && JSON.stringify(prev) !== JSON.stringify(next);
      rebased.push(changedUpstream ? next : group);
      continue;
    }
    // Absent from `next`. Two cases, and telling them apart is the whole of
    // Codex #3:
    //  - it WAS in `prev` → upstream removed it → drop (keeping it would
    //    resurrect a filter the rep no longer has applied).
    //  - it was NEVER in `prev` → a brand-new group the rep staged → KEEP.
    //    Upstream has said nothing about it, so there is nothing to rebase
    //    onto and no reason to delete the rep's in-progress work.
    if (!prevById.has(group.id)) rebased.push(group);
  }
  for (const group of nextApplied) {
    if (!prevById.has(group.id) && !draft.some(d => d.id === group.id)) rebased.push(group);
  }
  return rebased;
}

export function useFilterDraft({
  filter, onFiltersChange, groupLogic, onGroupLogicChange, fields, onApply, validate, replaceDraftOnApply,
}: UseFilterDraftOptions): FilterDraft {
  const [draftFilters, setDraftFiltersState] = useState<FilterGroup[]>(filter);
  // Set by `apply` (opt-in), consumed by the next applied-value change; an edit made first means the draft has moved on.
  const justAppliedRef = useRef(false);
  const setDraftFilters = useCallback<FilterDraft['setDraftFilters']>((updater) => {
    justAppliedRef.current = false;
    setDraftFiltersState(updater);
  }, []);
  const [draftGroupLogic, setDraftGroupLogic] = useState<GroupLogic>(groupLogic);

  // The VALUE (not reference) of the applied filters/groupLogic the draft was
  // last seeded from. Initialized inline — this is a running comparator, not
  // a StrictMode "ran once" gate, so there's no cleanup-only-reset trap to
  // guard against here (ported verbatim from FilterPopover's original doc).
  const lastAppliedKeyRef = useRef(filterDraftKey(filter, groupLogic));
  // The applied VALUES the draft was last seeded from, alongside the key
  // above — `rebaseDraft` needs the previous groups themselves, not just a
  // comparison key, to tell an upstream addition from an upstream edit.
  // Running comparators like `lastAppliedKeyRef`, not StrictMode "ran once"
  // gates, so initializing inline is correct here (no mount-effect reset).
  const prevAppliedRef = useRef(filter);
  const prevGroupLogicRef = useRef(groupLogic);
  useEffect(() => {
    const appliedKey = filterDraftKey(filter, groupLogic);
    if (appliedKey !== lastAppliedKeyRef.current) {
      lastAppliedKeyRef.current = appliedKey;
      const prevApplied = prevAppliedRef.current;
      const prevGroupLogic = prevGroupLogicRef.current;
      prevAppliedRef.current = filter;
      prevGroupLogicRef.current = groupLogic;
      // Codex #5: a DIRTY draft is rebased, not replaced. Dirtiness is read
      // inside the updater against the PREVIOUS applied value (the one the
      // draft was seeded from), which needs no extra ref and no `draftFilters`
      // dependency that would re-run this effect on every keystroke.
      const replace = justAppliedRef.current;
      justAppliedRef.current = false;
      setDraftFiltersState(draft => (
        replace || JSON.stringify(draft) === JSON.stringify(prevApplied)
          ? filter
          : rebaseDraft(prevApplied, filter, draft)
      ));
      setDraftGroupLogic(draft => (draft === prevGroupLogic ? groupLogic : draft));
    }
  }, [filter, groupLogic]);

  const isDirty = filterDraftKey(draftFilters, draftGroupLogic) !== filterDraftKey(filter, groupLogic);

  // Finding 2 (ported verbatim) — a condition whose field is absent from the
  // catalog (the R5 "legacy field" read-only row) has no type to validate
  // against and is skipped; it's already read-only in the popover, so its
  // stored value/operator can't be edited into a bad shape through this hook.
  const shapeError = draftFilters
    .flatMap(g => g.conditions)
    .reduce<string | null>((firstError, cond) => {
      if (firstError) return firstError;
      const fieldDef = fields.find(f => f.key === cond.field);
      if (!fieldDef) return null;
      // `valueStringsOf` (A1): a labeled-option field validates against its
      // stored values (record ids), never the display labels.
      return validateOperatorValueShape(fieldDef.type, cond.operator, cond.value, valueStringsOf(fieldDef));
    }, null)
    // A browser-evaluated (`andOnly`) field cannot take part in an OR: block Apply instead of silently ANDing.
    ?? andOnlyPlacementError(draftFilters, draftGroupLogic, fields);
  const validationError = shapeError ?? validate?.(draftFilters) ?? null;

  const apply = useCallback(() => {
    if (replaceDraftOnApply) justAppliedRef.current = true;
    if (onApply) {
      onApply(draftFilters, draftGroupLogic);
      return;
    }
    onFiltersChange(draftFilters);
    if (draftGroupLogic !== groupLogic) onGroupLogicChange(draftGroupLogic);
  }, [draftFilters, draftGroupLogic, groupLogic, onApply, onFiltersChange, onGroupLogicChange, replaceDraftOnApply]);

  // GTMOPS-2216 — the banner's "Discard" action (and, by extension,
  // FilterPopover's own reopen-with-a-fresh-draft precedent no longer
  // applies once a draft persists across close): revert the draft to
  // whatever is currently applied, discarding the unapplied edit. Does NOT
  // touch `lastAppliedKeyRef` — the applied value hasn't changed, only the
  // draft has, so `isDirty` reads false immediately without needing the
  // reseed effect to run.
  const discard = useCallback(() => {
    setDraftFilters(filter);
    setDraftGroupLogic(groupLogic);
  }, [setDraftFilters, filter, groupLogic]);

  return {
    draftFilters,
    draftGroupLogic,
    setDraftFilters,
    setDraftGroupLogic,
    isDirty,
    validationError,
    apply,
    discard,
  };
}
